import React, { useEffect, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "hooks";

import { Routes } from "../../constants";

type Status = "loading" | "success" | "error";

interface Props {
  promise: Promise<unknown>;
  dealId: number;
  onRefetch?: () => void;
  onSubmit: (result: unknown) => void;
  onCancel: () => void;
  isMinimized?: boolean;
}

export const LookThroughStatusPrompt: React.FC<Props> = ({ promise, dealId, onRefetch, onCancel, isMinimized }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;

    promise
      .then(() => {
        if (!mountedRef.current) return;
        setStatus("success");
      })
      .catch((error: unknown) => {
        if (!mountedRef.current) return;
        setStatus("error");
        const message = error instanceof Error ? error.message : "Something went wrong.";
        setErrorMessage(message);
      });

    return () => {
      mountedRef.current = false;
    };
  }, [promise]);

  const isLookThroughRoute = Boolean(matchPath(pathname, Routes.dealLookThrough));
  const successConfig = isLookThroughRoute
    ? onRefetch && { label: "Refresh", action: onRefetch }
    : { label: "Open Look Through", action: () => navigate({ route: "dealLookThrough", params: { deal_id: dealId } }) };

  const handleSuccessAction = () => {
    successConfig && successConfig.action();
    onCancel();
  };

  if (isMinimized) {
    return (
      <Stack direction="row" gap="8px" alignItems="center" sx={{ px: "16px", py: "8px" }}>
        {status === "loading" && (
          <>
            <CircularProgress size={14} />
            <Typography sx={{ fontSize: "12px", color: "#667085" }}>Extracting...</Typography>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircleOutlineIcon sx={{ color: "#12B76A", fontSize: "16px" }} />
            <Typography sx={{ fontSize: "16px", color: "#667085" }}>Done</Typography>
          </>
        )}
        {status === "error" && (
          <>
            <ErrorOutlineIcon sx={{ color: "#F04438", fontSize: "16px" }} />
            <Typography sx={{ fontSize: "16px", color: "#F04438" }}>Failed</Typography>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="12px" sx={{ p: "16px" }}>
      {status === "loading" && (
        <Stack direction="row" gap="12px" alignItems="center">
          <CircularProgress size={20} />
          <Stack gap="4px">
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>AI is extracting Look Through data...</Typography>
          </Stack>
        </Stack>
      )}
      {status === "success" && (
        <Stack gap="12px">
          <Stack direction="row" gap="12px" alignItems="center">
            <CheckCircleOutlineIcon sx={{ color: "#12B76A" }} />
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Look Through data extracted.</Typography>
          </Stack>
          {successConfig && (
            <Stack direction="row" justifyContent="flex-end">
              <Button size="sm" variant="contained" onClick={handleSuccessAction}>
                {successConfig.label}
              </Button>
            </Stack>
          )}
        </Stack>
      )}
      {status === "error" && (
        <Stack gap="8px">
          <Stack direction="row" gap="12px" alignItems="flex-start">
            <ErrorOutlineIcon sx={{ color: "#F04438", mt: "2px" }} />
            <Stack gap="4px">
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Extraction failed.</Typography>
              <Typography sx={{ fontSize: "12px", color: "#667085" }}>{errorMessage}</Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Button size="sm" variant="outlined" onClick={onCancel}>
              Close
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
